import { api } from './api';
import type {
  CreateReminderRequest,
  UpdateReminderRequest,
  ReminderFilters,
  RemindersResponse,
  ReminderResponse,
  SnoozeResponse,
} from '../types/reminder';

const REMINDERS_URL = '/reminders';

/**
 * Get all reminders with filters
 */
export const getReminders = async (filters?: ReminderFilters): Promise<RemindersResponse> => {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.status?.length) {
      params.append('status', filters.status.join(','));
    }
    if (filters.type) {
      params.append('type', filters.type);
    }
    if (filters.upcoming) {
      params.append('upcoming', 'true');
    }
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    if (filters.sortOrder) {
      params.append('sortOrder', filters.sortOrder);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
  }

  const queryString = params.toString();
  const url = queryString ? `${REMINDERS_URL}?${queryString}` : REMINDERS_URL;

  const response = await api.get<RemindersResponse>(url);
  return response.data;
};

/**
 * Get a single reminder
 */
export const getReminder = async (id: string): Promise<ReminderResponse> => {
  const response = await api.get<ReminderResponse>(`${REMINDERS_URL}/${id}`);
  return response.data;
};

/**
 * Create a new reminder
 */
export const createReminder = async (data: CreateReminderRequest): Promise<ReminderResponse> => {
  const response = await api.post<ReminderResponse>(REMINDERS_URL, data);
  return response.data;
};

/**
 * Update a reminder
 */
export const updateReminder = async (
  id: string,
  data: UpdateReminderRequest
): Promise<ReminderResponse> => {
  const response = await api.patch<ReminderResponse>(`${REMINDERS_URL}/${id}`, data);
  return response.data;
};

/**
 * Delete a reminder
 */
export const deleteReminder = async (id: string): Promise<{ success: boolean }> => {
  const response = await api.delete(`${REMINDERS_URL}/${id}`);
  return response.data;
};

/**
 * Snooze a reminder
 */
export const snoozeReminder = async (
  id: string,
  minutes: number
): Promise<SnoozeResponse> => {
  const response = await api.post<SnoozeResponse>(`${REMINDERS_URL}/${id}/snooze`, { minutes });
  return response.data;
};

/**
 * Dismiss a reminder
 */
export const dismissReminder = async (id: string): Promise<ReminderResponse> => {
  const response = await api.post<ReminderResponse>(`${REMINDERS_URL}/${id}/dismiss`);
  return response.data;
};

export default {
  getReminders,
  getReminder,
  createReminder,
  updateReminder,
  deleteReminder,
  snoozeReminder,
  dismissReminder,
};
